import styled from 'styled-components';

import { gql } from '@apollo/client';
import { Chip } from '@mui/material';
import { orderBy } from 'lodash';
import { NextPage } from 'next';
import { FOOTER_QUERY } from '../components/footer';
import { Footer } from '../components/footer';
import { HEADER_QUERY, Header } from '../components/header';
import { Col, ResponsiveContainer, Row } from '../components/responsive';
import { ContentStackClient } from '../lib/contentstack';
import { AllChangelog } from '../types/contentstack';
import { IPageProps } from '../types/page';
import { dateAndTime } from '../utils/dateAndTime';
import { mediaScreen } from '../utils/mediaScreen';

export const Page: NextPage<
  IPageProps<any> & { all_changelog: AllChangelog }
> = ({ header, footer, all_changelog }) => {
  const changelogs = all_changelog?.items || [];

  // console.log(changelogs);

  return (
    <>
      <Header
        pageTitle="Changelog"
        pageDescription="Additions and changes to the Mapped Platform"
        {...header}
      />

      <ResponsiveContainer>
        <Row>
          <Content md={12}>
            <Title>Changelog</Title>
            <Text>Additions and changes to the Mapped Platform</Text>
          </Content>
        </Row>

        {changelogs.map((changelog) => {
          return (
            <Changelog key={changelog?.system?.uid}>
              <div className="details">
                <span className="date">{dateAndTime(changelog?.date)}</span>

                {changelog?.system?.tags?.map((tag) => (
                  <Chip key={tag} label={tag} />
                ))}
              </div>

              <h2>{changelog?.title}</h2>

              <div
                dangerouslySetInnerHTML={{
                  __html: changelog?.description || '',
                }}
              />
            </Changelog>
          );
        })}
      </ResponsiveContainer>

      <Footer sections={footer?.sections} />
    </>
  );
};

export async function getServerSideProps() {
  try {
    const { data } = await ContentStackClient().query<
      IPageProps<any> & { all_changelog: AllChangelog }
    >({
      query: PAGE_QUERY,
    });

    const sortedItems = orderBy(
      data?.all_changelog?.items,
      (e) => new Date(e?.date),
      'desc'
    );

    return {
      props: {
        ...data,
        all_changelog: {
          ...data?.all_changelog,
          items: sortedItems,
        },
      },
    };
  } catch (e) {
    return { props: {} };
  }
}

const PAGE_QUERY = gql`
  query changelogQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    all_changelog {
      items {
        title
        description
        date
        system {
          uid
          tags
        }
      }
    }
  }
`;

const Content = styled(Col)`
  align-items: center;
  display: flex;
  flex-direction: column;

  ${mediaScreen('xs', 'md')} {
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  font-size: 67px;
  color: white;
  font-weight: bold;
  margin-bottom: 0;
  margin-top: 150px;
  text-align: center;

  ${mediaScreen('xs', 'md')} {
    font-size: 35px;
    text-align: left;
    margin-bottom: 24px;
  }
`;

const Text = styled.p`
  color: white;
  font-size: 22px;
  line-height: 32px;
  margin: 0 auto;
  margin-bottom: 64px;
  text-align: center;
  max-width: 630px;

  ${mediaScreen('xs', 'md')} {
    font-size: 17px;
    margin: 0;
    margin-bottom: 64px;
    text-align: left;
  }
`;

const Changelog = styled.div`
  background: #262f40;
  border-radius: 20px;
  padding: 25px;
  margin-bottom: 60px;

  .details {
    display: flex;
    align-items: center;

    .date {
      font-size: 14px;
    }

    .MuiChip-root {
      background: none;
      border: 1px solid ${(props) => props.theme.colors.teal};
      color: ${(props) => props.theme.colors.teal};
      margin-left: 15px;
      border-radius: 3px;
      height: 24px;
      padding: 0 0px;

      .MuiChip-label {
        padding: 0 8px;
      }
    }
  }

  a {
    color: ${(props) => props.theme.colors.teal};

    :hover {
      text-decoration: underline;
    }
  }
`;

export default Page;
